import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileUp, Loader2, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { FoodSelect, PageHead, apiErr, fmtDate, thisYM, useMerendaMeta } from "@/merenda/common";
import { KindPanel } from "@/merenda/Importar";

const MenuDialog = ({ date, meal, mealLabel, current, foods, scopeParams, onClose, onDone }) => {
  const [prep, setPrep] = useState(current?.preparation || "");
  const [ids, setIds] = useState(current?.food_ids || []);
  const [keep, setKeep] = useState(false);
  const fm = Object.fromEntries(foods.map((f) => [f.id, f]));
  const save = async () => {
    try {
      await api.post("/merenda/menu", { date, meal, preparation: prep, food_ids: ids }, { params: scopeParams });
      if (keep && ids.length && prep.trim()) await api.post("/merenda/recipes", { name: prep, food_ids: ids }, { params: scopeParams });
      toast.success("Cardápio salvo"); onDone();
    }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="menu-dialog">
        <DialogHeader><DialogTitle>{fmtDate(date)} — {mealLabel}</DialogTitle></DialogHeader>
        <div><Label>Preparação</Label><Input value={prep} onChange={(e) => setPrep(e.target.value)} placeholder="Ex.: Ensopadinho de frango com quiabo, arroz, feijão" data-testid="menu-preparation" /></div>
        <div><Label>Alimentos da preparação (base das sugestões)</Label>
          <FoodSelect foods={foods} value="" onChange={(v) => v && !ids.includes(v) && setIds([...ids, v])} testid="menu-add-food" />
          <div className="mt-2 flex flex-wrap gap-1">{ids.map((id) => <span key={id} className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-1 text-xs text-orange-800">{fm[id]?.name}<button onClick={() => setIds(ids.filter((x) => x !== id))}><X className="h-3 w-3" /></button></span>)}</div>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={keep} onChange={(e) => setKeep(e.target.checked)} data-testid="menu-keep-recipe" />Guardar como receita (usada nas próximas importações)</label>
        <DialogFooter><Button onClick={save} data-testid="menu-save">Salvar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Cardapio() {
  const { meta, foods, scopeParams, canManage, reload } = useMerendaMeta();
  const [importing, setImporting] = useState(false);
  const [ym, setYm] = useState(thisYM());
  const [sum, setSum] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/merenda/summary", { params: { ym, ...scopeParams } }).then((r) => setSum(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { if (meta) load(); }, [meta, ym]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!meta || !sum) return <div className="grid min-h-[40vh] place-items-center"><Loader2 className="h-8 w-8 animate-spin text-blue-700" /></div>;
  const fm = Object.fromEntries(foods.map((f) => [f.id, f]));
  const menuOf = (d, k) => sum.menu.find((m) => m.date === d && m.meal === k);
  return (
    <div data-testid="cardapio-page">
      <PageHead title="Cardápio" subtitle="O cardápio orienta as sugestões; o consumo real vem dos lançamentos das cozinheiras.">
        <Input type="month" value={ym} onChange={(e) => setYm(e.target.value)} className="w-44" data-testid="cardapio-month" />
        {canManage && <Button className="bg-orange-500 hover:bg-orange-600" onClick={() => setImporting(true)} data-testid="cardapio-import-seeduc"><FileUp className="mr-2 h-4 w-4" />Importar Cardápio SEEDUC</Button>}
      </PageHead>
      {importing && (
        <Dialog open onOpenChange={(o) => !o && setImporting(false)}>
          <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="cardapio-import-dialog">
            <DialogHeader><DialogTitle>Importar Cardápio SEEDUC</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-500">Envie o PDF ou as fotos do cardápio do mês. A IA lê cada dia e associa os alimentos de cada preparação (receitas já salvas são usadas sem IA). Só os dias do arquivo são substituídos.</p>
            <KindPanel kind="MENU" meta={meta} foods={foods} scopeParams={scopeParams} onFoodsChanged={reload} onConfirmed={load} />
          </DialogContent>
        </Dialog>
      )}
      <div className="grid gap-3">
        {sum.days.map((d) => (
          <Card key={d} className="border-slate-200 p-4" data-testid={`cardapio-day-${d}`}>
            <div className="mb-2 font-display font-bold text-slate-800">{fmtDate(d)}</div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {meta.meals.map(([k, l]) => {
                const m = menuOf(d, k);
                return (
                  <div key={k} className="rounded-lg bg-slate-50 p-2 text-sm">
                    <div className="flex items-center justify-between text-xs font-bold uppercase text-slate-500">{l}
                      {canManage && <button onClick={() => setEdit({ date: d, meal: k, label: l, current: m })} data-testid={`cardapio-edit-${d}-${k}`}><Pencil className="h-3.5 w-3.5" /></button>}
                    </div>
                    <div className="mt-1 text-slate-700">{m?.preparation || <span className="text-slate-400">—</span>}</div>
                    {m?.food_ids?.length > 0 && <div className="mt-1 text-xs text-slate-500">{m.food_ids.map((id) => fm[id]?.name).filter(Boolean).join(", ")}</div>}
                  </div>
                );
              })}
            </div>
          </Card>
        ))}
      </div>
      {edit && <MenuDialog date={edit.date} meal={edit.meal} mealLabel={edit.label} current={edit.current} foods={foods} scopeParams={scopeParams} onClose={() => setEdit(null)} onDone={() => { setEdit(null); load(); }} />}
    </div>
  );
}
